import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import { PCNode } from '../MineModel/Nodes';
import PCSpool from '../MineModel/Spools/PCSpool';

export type NodeClickHandler = (node: PCNode | undefined) => void;
export type SpoolClickHandler = (
  spool: PCSpool,
  clickPoint?: { x: number; y: number; z: number },
) => void;

interface ToolHandlerContextValue {
  setNodeClickHandler: (handler: NodeClickHandler | null) => void;
  setSpoolClickHandler: (handler: SpoolClickHandler | null) => void;
  nodeClickHandlerRef: React.MutableRefObject<NodeClickHandler | null>;
  spoolClickHandlerRef: React.MutableRefObject<SpoolClickHandler | null>;
  hasActiveTool: boolean;
}

const ToolHandlerContext = createContext<ToolHandlerContextValue | null>(null);

export const ToolHandlerProvider = ({ children }: { children: React.ReactNode }) => {
  const nodeClickHandlerRef = useRef<NodeClickHandler | null>(null);
  const spoolClickHandlerRef = useRef<SpoolClickHandler | null>(null);
  const [hasNodeHandler, setHasNodeHandler] = useState(false);
  const [hasSpoolHandler, setHasSpoolHandler] = useState(false);

  const setNodeClickHandler = useCallback((handler: NodeClickHandler | null) => {
    nodeClickHandlerRef.current = handler;
    setHasNodeHandler(!!handler);
  }, []);

  const setSpoolClickHandler = useCallback((handler: SpoolClickHandler | null) => {
    spoolClickHandlerRef.current = handler;
    setHasSpoolHandler(!!handler);
  }, []);

  const value = useMemo<ToolHandlerContextValue>(() => ({
    setNodeClickHandler,
    setSpoolClickHandler,
    nodeClickHandlerRef,
    spoolClickHandlerRef,
    hasActiveTool: hasNodeHandler || hasSpoolHandler,
  }), [setNodeClickHandler, setSpoolClickHandler, hasNodeHandler, hasSpoolHandler]);

  return (
    <ToolHandlerContext.Provider value={value}>
      {children}
    </ToolHandlerContext.Provider>
  );
};

export const useToolHandlers = (): ToolHandlerContextValue => {
  const ctx = useContext(ToolHandlerContext);
  if (!ctx) throw new Error('useToolHandlers must be used within a ToolHandlerProvider');
  return ctx;
};
